/**
 * FathomVoyage — Module 3: Route Cost Comparator v8
 * Hub-based routing via Cloudflare Worker.
 * Three-step Recalculate modal + left rail with source-tag taxonomy.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { fetchRouteCosts, fetchHubs, fetchBunkerGlobal20, fmtM } from '../../services/routes/calculator.js';
import { getNextPositions } from '../../services/routes/cargoPositions.js';
import ProWaitlistModal from '../BunkerPrices/ProWaitlistModal.jsx';

// ─── Constants ────────────────────────────────────────────────────────────────
const PORT_COSTS_DEFAULT  = 80_000;   // Indicative — no vessel-type data
const CANAL_DUES_DEFAULT  = 50_000;   // Indicative — varies by canal / vessel
const SESSION_FLAG        = 'fv_route_calc_modal_seen_session';
const MODAL_FUEL_OPTS     = ['VLSFO', 'IFO380', 'LNG'];

const CHOKEPOINTS = [
  { name: 'Suez Canal',        lat:  30.5, lon:  32.3 },
  { name: 'Bab-el-Mandeb',     lat:  12.6, lon:  43.4 },
  { name: 'Strait of Hormuz',  lat:  26.6, lon:  56.3 },
  { name: 'Malacca Strait',    lat:   1.3, lon: 103.8 },
  { name: 'Panama Canal',      lat:   9.1, lon: -79.7 },
  { name: 'Cape of Good Hope', lat: -34.4, lon:  18.5 },
  { name: 'Gibraltar',         lat:  35.9, lon:  -5.5 },
];

const HUB_TO_REGION = {
  AG:            'arabian_gulf',
  N_EUROPE:      'north_europe',
  MED:           'mediterranean',
  SINGAPORE:     'singapore',
  FAR_EAST:      'china_korea',
  JAPAN_KOREA:   'japan',
  AUSTRALIA_EAST:'australia',
  AUSTRALIA_WEST:'australia',
  US_EAST:       'us_east_coast',
  US_GULF:       'us_gulf',
  W_AFRICA:      'west_africa',
  S_ASIA:        'india',
  E_AFRICA:      'east_africa',
  RED_SEA:       'red_sea',
  BLACK_SEA:     'mediterranean',
  BALTIC:        'north_europe',
  NORTH_SEA:     'north_europe',
  CARIBBEAN:     'us_gulf',
  S_AM_EAST:     'us_east_coast',
  S_AM_WEST:     'us_gulf',
  US_WEST:       'us_east_coast',
};

const fmtDays = d => `${(+(d) || 0).toFixed(1)}d`;
const fmtNM   = d => `${Math.round(+(d) || 0).toLocaleString()} NM`;

function fmtCost(val) {
  if (!val && val !== 0) return '—';
  if (val >= 1_000_000) return '$' + (val / 1_000_000).toFixed(2) + 'M';
  if (val >= 1_000)     return '$' + Math.round(val / 1_000) + 'K';
  return '$' + val.toLocaleString();
}

function fmtNum(val) {
  if (val === null || val === undefined || val === '') return '';
  return Number(val).toLocaleString('en-US');
}

function parseNum(str) {
  if (str === '' || str === null || str === undefined) return null;
  const n = parseFloat(String(str).replace(/,/g, ''));
  return isNaN(n) ? null : n;
}

function fmtNm(val) { return (+(val) || 0).toLocaleString() + ' NM'; }

function distNM(lat1, lon1, lat2, lon2) {
  const R    = 3440.065;
  const toR  = x => x * Math.PI / 180;
  const dLat = toR(lat2 - lat1);
  const dLon = toR(lon2 - lon1);
  const a    = Math.sin(dLat / 2) ** 2
             + Math.cos(toR(lat1)) * Math.cos(toR(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function routeNear(waypoints, lat, lon, threshold = 600) {
  return waypoints.some(w => Array.isArray(w) && distNM(w[0], w[1], lat, lon) < threshold);
}

// ─── CSS injection (keep existing shimmer + input styles) ─────────────────────
if (typeof document !== 'undefined' && !document.getElementById('fv-r7-css')) {
  const s = document.createElement('style');
  s.id = 'fv-r7-css';
  s.textContent = `
    .fv7-select {
      background-color: #0d1929;
      color: #e2e8f0;
      border: 1px solid #2d3748;
      border-radius: 6px;
      padding: 8px 36px 8px 12px;
      width: 100%;
      appearance: none;
      -webkit-appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 12px center;
      cursor: pointer;
      font-family: inherit;
      font-size: 14px;
      box-sizing: border-box;
    }
    .fv7-select option { background-color: #0d1929; color: #e2e8f0; }
    .fv7-select:focus { outline: none; border-color: #e9a020; }
    @keyframes fv7shimmer {
      0%   { background-position: -400px 0; }
      100% { background-position:  400px 0; }
    }
    .fv7-shimmer {
      background: linear-gradient(90deg, #1a2332 25%, #243044 50%, #1a2332 75%);
      background-size: 800px 100%;
      animation: fv7shimmer 1.5s infinite;
      border-radius: 4px;
    }
  `;
  document.head.appendChild(s);
}

// ─── Source tag component ─────────────────────────────────────────────────────
function SourceTag({ type }) {
  if (type === 'live') {
    return (
      <span className="src-tag src-tag-live">
        <span className="src-tag-dot" />
        Live
      </span>
    );
  }
  if (type === 'indicative') {
    return <span className="src-tag src-tag-indicative">Indicative</span>;
  }
  return <span className="src-tag src-tag-input">Input</span>;
}

// ─── Numeric input with comma formatting + modified-dot ───────────────────────
function NumericInput({
  value, onChange, placeholder = '', min, max, step = 1,
  isModified = false, disabled = false, prefix = '',
  className = 'recalc-num-input',
}) {
  const [raw, setRaw]         = useState(value !== null && value !== '' ? String(value) : '');
  const [focused, setFocused] = useState(false);
  const prevValueRef          = useRef(value);

  useEffect(() => {
    if (prevValueRef.current !== value && !focused) {
      setRaw(value !== null && value !== '' ? String(value) : '');
      prevValueRef.current = value;
    }
  }, [value, focused]);

  const displayed = focused
    ? raw
    : (raw === '' ? '' : (() => {
        const n = parseFloat(raw.replace(/,/g, ''));
        return isNaN(n) ? raw : n.toLocaleString('en-US');
      })());

  const handleChange = (e) => {
    const v = e.target.value.replace(/,/g, '');
    setRaw(v);
    const n = parseFloat(v);
    onChange(isNaN(n) ? null : n);
  };

  const handleBlur = () => {
    setFocused(false);
    const n = parseFloat(raw.replace(/,/g, ''));
    if (!isNaN(n)) {
      if (min !== undefined && n < min) { onChange(min); setRaw(String(min)); return; }
      if (max !== undefined && n > max) { onChange(max); setRaw(String(max)); return; }
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {prefix && (
        <span style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          color: '#8892a5', fontSize: 13, fontFamily: 'var(--font-mono)',
          pointerEvents: 'none',
        }}>
          {prefix}
        </span>
      )}
      <input
        type="text"
        inputMode="decimal"
        value={displayed}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        style={{ paddingLeft: prefix ? 22 : undefined }}
        onChange={handleChange}
        onFocus={() => { setFocused(true); setRaw(raw.replace(/,/g, '')); }}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp')   { e.preventDefault(); const n = parseFloat(raw.replace(/,/g, '') || '0') + step; onChange(n); setRaw(String(n)); }
          if (e.key === 'ArrowDown') { e.preventDefault(); const n = parseFloat(raw.replace(/,/g, '') || '0') - step; onChange(n); setRaw(String(n)); }
        }}
      />
    </div>
  );
}

// ─── Sidebar label ────────────────────────────────────────────────────────────
const SLabel = ({ n, children }) => (
  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
    {n && <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', background: 'var(--indigo-light)', color: 'var(--indigo)', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{n}</span>}
    {children}
  </div>
);

// ─── Route Map ────────────────────────────────────────────────────────────────
function RouteMap({ result, fromHub, toHub, hubs, selectedKey, onSelectKey, isMobile }) {
  const mapRef   = useRef(null);
  const leafRef  = useRef(null);
  const layersRef = useRef({});
  const readyRef = useRef(false);

  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const l = document.createElement('link');
      l.id = 'leaflet-css'; l.rel = 'stylesheet';
      l.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(l);
    }

    const init = () => {
      if (leafRef.current || !mapRef.current || !window.L) return;
      setTimeout(() => {
        if (!mapRef.current) return;
        try {
          const m = window.L.map(mapRef.current, {
            center: [20, 50], zoom: 2,
            minZoom: 2, maxZoom: 3,
            zoomControl: true, attributionControl: true, preferCanvas: true,
          });
          window.L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png', { maxZoom: 6, attribution: '© OpenStreetMap contributors, © CARTO' }).addTo(m);
          window.L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png', { maxZoom: 6 }).addTo(m);
          leafRef.current  = m;
          readyRef.current = true;
          draw(m);
        } catch (e) { console.warn('[RouteMap]', e); }
      }, 200);
    };

    if (window.L) { init(); return; }
    if (!document.getElementById('leaflet-js')) {
      const sc = document.createElement('script');
      sc.id = 'leaflet-js'; sc.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      sc.onload = init;
      document.head.appendChild(sc);
    }

    return () => {
      if (leafRef.current) {
        try { leafRef.current.remove(); } catch {}
        leafRef.current  = null;
        readyRef.current = false;
      }
    };
  }, []);

  useEffect(() => {
    if (readyRef.current && leafRef.current) draw(leafRef.current);
  }, [result, selectedKey, fromHub, toHub]);

  function draw(map) {
    const L = window.L;
    if (!L || !map) return;
    Object.values(layersRef.current).forEach(l => { try { l.remove(); } catch {} });
    layersRef.current = {};
    if (!result?.variants?.length) return;

    const variants  = result.variants;
    const allPoints = variants.flatMap(v => (v.waypoints || []).filter(w => Array.isArray(w) && isFinite(w[0]) && isFinite(w[1])));

    variants.forEach(v => {
      const isSelected = v.key === selectedKey;
      const wps        = (v.waypoints || []).filter(w => Array.isArray(w) && w.length >= 2);
      if (wps.length < 2) return;
      try {
        const line = L.polyline(wps, {
          color:     isSelected ? '#e9a020' : '#64748B',
          weight:    isSelected ? 4 : 2,
          opacity:   isSelected ? 0.9 : 0.6,
          dashArray: isSelected ? null : '8 6',
        })
          .bindPopup(`<div style="font-family:sans-serif;padding:8px;min-width:160px"><b style="font-size:14px">${v.display_name}</b><br><span style="font-family:monospace;color:#e9a020;font-size:16px">${fmtM(v.total_cost)}</span><br><span style="color:#94A3B8;font-size:12px">${fmtNM(v.distance_nm)} · ${fmtDays(v.voyage_days)}</span></div>`)
          .on('click', () => onSelectKey && onSelectKey(isSelected ? null : v.key))
          .addTo(map);
        layersRef.current[`route_${v.key}`] = line;
        if (isSelected) line.bringToFront();
      } catch (e) { console.warn(e); }
    });

    const fromObj = hubs.find(h => h.id === fromHub);
    const toObj   = hubs.find(h => h.id === toHub);
    [fromObj, toObj].filter(Boolean).forEach(hub => {
      try {
        const m = L.circleMarker([hub.lat, hub.lon], { radius: 8, fillColor: '#e9a020', color: '#ffffff', weight: 2, fillOpacity: 0.9 })
          .bindTooltip(hub.name).addTo(map);
        layersRef.current[`hub_${hub.id}`] = m;
      } catch (e) { console.warn(e); }
    });

    CHOKEPOINTS.forEach(cp => {
      if (!routeNear(allPoints, cp.lat, cp.lon)) return;
      try {
        const icon = L.divIcon({ html: `<div style="color:#EF4444;font-size:12px;line-height:1;transform:translate(-50%,-50%)">◆</div>`, className: '', iconSize: [12, 12], iconAnchor: [6, 6] });
        layersRef.current[`cp_${cp.name}`] = L.marker([cp.lat, cp.lon], { icon }).bindTooltip(cp.name).addTo(map);
      } catch (e) { console.warn(e); }
    });

    try {
      if (allPoints.length >= 2) map.fitBounds(allPoints, { padding: [40, 40], maxZoom: 6 });
    } catch {}
  }

  return <div ref={mapRef} style={{ height: isMobile ? 240 : 320, background: '#0d1929' }} />;
}

// ─── Next Cargo inner ─────────────────────────────────────────────────────────
function NextCargoInner({ positions, toHub, hubs, isMobile }) {
  const hubName = hubs.find(h => h.id === toHub)?.name || toHub;
  return (
    <div className="fv-card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
          Next cargo position — after discharging at {hubName}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 3 }}>2025/2026 trade flow data</div>
      </div>
      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(220px,1fr))', gap: 14 }}>
        {positions.map((pos, i) => (
          <div key={i} style={{
            padding: 16, borderRadius: 'var(--radius-lg)',
            border: `1px solid ${i === 0 ? 'var(--blue-mid)' : 'var(--border)'}`,
            background: i === 0 ? 'var(--blue-light)' : 'var(--card)',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{ fontSize: 22 }}>{pos.flag}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{pos.label}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{pos.cargo}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>~{pos.approxBallastDays}d ballast · Key ports: {pos.ports.join(', ')}</div>
            {pos.note && <div style={{ fontSize: 12, color: 'var(--blue)', fontWeight: 500 }}>💡 {pos.note}</div>}
          </div>
        ))}
      </div>
      <div style={{ padding: '8px 22px 14px', fontSize: 11, color: 'var(--muted)' }}>
        Source: EIA (Jan 2026) · Kpler Q1 2026 · Argus Media. Indicative — confirm with brokers.
      </div>
    </div>
  );
}

function NextCargoSection({ toHub, hubs, user, onSignIn, isMobile }) {
  const region  = HUB_TO_REGION[toHub] || null;
  const nextPos = useMemo(() => region ? getNextPositions(region, 'vlcc') : [], [region]);
  if (!nextPos.length) return null;

  return (
    <div style={{ padding: isMobile ? '0 14px 16px' : '0 28px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '8px 0 16px' }}>
        <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right,#075985,transparent)' }} />
        <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>Market Intelligence</span>
        <div style={{ flex: 1, height: 1, background: 'linear-gradient(to left,#075985,transparent)' }} />
      </div>
      <div style={{ position: 'relative' }}>
        <div style={!user ? { filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' } : {}}>
          <NextCargoInner positions={nextPos} toHub={toHub} hubs={hubs} isMobile={isMobile} />
        </div>
        {!user && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(10,14,23,0.45)', borderRadius: 'var(--radius-lg)' }}>
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '32px 40px', textAlign: 'center', maxWidth: 340 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Next Cargo Intelligence</div>
              <div style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 20 }}>See cargo opportunities after discharge</div>
              <button onClick={onSignIn} style={{ width: '100%', padding: '11px 20px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--grad-navy)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 8 }}>
                Sign in with LinkedIn
              </button>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Free · No card required</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Route Grid ───────────────────────────────────────────────────────────────
const RG_ROWS = [
  { key: 'bunker_cost', label: 'Bunker',  dot: '#3B82F6' },
  { key: 'canal_cost',  label: 'Canal',   dot: '#8B5CF6' },
  { key: 'awrp_cost',   label: 'AWRP',    dot: '#EF4444' },
  { key: 'tc_cost',     label: 'TC',      dot: '#0EA5E9' },
  { key: 'port_cost',   label: 'Port',    dot: '#6B7280' },
];

function RouteGrid({ variants, selectedKey, onSelectKey, isMobile }) {
  const gridRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const handler = () => { if (el.scrollLeft > 12) setScrolled(true); };
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, []);

  const cols    = variants.length;
  const bestKey = variants[0]?.key;
  const PAD     = isMobile ? '8px 10px' : '9px 14px';
  const LBLW    = isMobile ? 88 : 108;
  const BORDER  = '1px solid #1e2d44';

  const colBg = (v) => {
    if (v.key === selectedKey) return 'rgba(233,160,32,0.07)';
    if (v.key === bestKey)     return 'rgba(233,160,32,0.025)';
    return 'transparent';
  };

  const labelBase = {
    position: 'sticky', left: 0, zIndex: 2,
    background: '#0a1525',
    borderRight: BORDER, borderBottom: BORDER,
    display: 'flex', alignItems: 'center', gap: 6,
    padding: PAD,
    fontFamily: "'Figtree', sans-serif",
    fontSize: 'max(13px, var(--rg-label, 13px))',
    color: '#8892a5', fontWeight: 500,
    whiteSpace: 'nowrap',
  };

  const valStyle = (v, extra = {}) => ({
    borderLeft: BORDER, borderBottom: BORDER,
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
    padding: PAD,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 'var(--rg-val, 13px)',
    color: '#e2e8f0',
    background: colBg(v),
    transition: 'background 0.15s',
    cursor: 'pointer',
    ...extra,
  });

  const anim = (delay) => ({ animation: `rowFadeIn 0.28s ease ${delay}ms both` });
  const handleClick = (v) => onSelectKey(v.key === selectedKey ? null : v.key);

  return (
    <div style={{ position: 'relative' }}>
      {isMobile && !scrolled && cols > 1 && (
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 36, background: 'linear-gradient(to right, transparent, rgba(10,17,37,0.9))', pointerEvents: 'none', zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 10 }}>
          <span style={{ color: '#e9a020', fontSize: 16, fontFamily: "'JetBrains Mono', monospace" }}>›</span>
        </div>
      )}
      <div
        ref={gridRef}
        className="route-grid"
        data-cols={String(cols)}
        style={{ display: 'grid', gridTemplateColumns: `${LBLW}px repeat(${cols}, 1fr)`, overflowX: 'auto', WebkitOverflowScrolling: 'touch', minWidth: 0 }}
      >
        {/* Header row */}
        <div style={{ ...labelBase, fontSize: 11, color: '#3d4f6b', letterSpacing: '0.8px', textTransform: 'uppercase', borderBottom: '2px solid #1e2d44' }}>ROUTE</div>
        {variants.map((v) => {
          const isBest = v.key === bestKey, isSelected = v.key === selectedKey;
          return (
            <div key={`hdr-${v.key}`} onClick={() => handleClick(v)} style={{ borderLeft: BORDER, borderBottom: '2px solid #1e2d44', background: colBg(v), cursor: 'pointer', transition: 'background 0.15s', padding: isMobile ? '10px 10px' : '11px 14px', display: 'flex', flexDirection: 'column', gap: 3, position: 'relative', minWidth: 0 }}>
              {isBest && <span style={{ position: 'absolute', top: 7, right: 7, fontSize: 11, fontWeight: 700, letterSpacing: '0.4px', color: '#e9a020', background: 'rgba(233,160,32,0.1)', padding: '2px 5px', borderRadius: 8, border: '1px solid rgba(233,160,32,0.28)', fontFamily: "'JetBrains Mono', monospace" }}>CHEAPEST</span>}
              {variants.length > 1 && (
                <span style={{ fontFamily: "'Fraunces', serif", fontSize: isMobile ? 11 : 13, fontWeight: 700, color: isSelected ? '#e9a020' : '#e2e8f0', paddingRight: isBest ? 54 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.display_name}</span>
              )}
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: isMobile ? 11 : 12, color: '#8892a5', whiteSpace: 'nowrap' }}>{fmtNm(v.distance_nm)}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: isMobile ? 11 : 12, color: '#8892a5', whiteSpace: 'nowrap' }}>{fmtDays(v.voyage_days)}</span>
            </div>
          );
        })}

        {/* Total cost */}
        <div style={{ ...labelBase, fontSize: isMobile ? 15 : 20, fontWeight: 700, color: '#6b7a99', textTransform: 'uppercase', letterSpacing: '0.4px', ...anim(0) }}>Total</div>
        {variants.map((v) => (
          <div key={`total-${v.key}`} onClick={() => handleClick(v)} style={valStyle(v, { padding: isMobile ? '10px 10px' : '10px 14px', ...anim(0) })}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: `var(--rg-total, ${isMobile ? 18 : 22}px)`, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1, color: v.key === bestKey ? '#e9a020' : '#e2e8f0' }}>{fmtCost(v.total_cost)}</span>
          </div>
        ))}

        {/* Cost breakdown rows */}
        {RG_ROWS.map((row, idx) => (
          <React.Fragment key={row.key}>
            <div style={{ ...labelBase, ...anim((idx + 1) * 45) }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: row.dot, flexShrink: 0, display: 'inline-block' }} />
              {row.label}
            </div>
            {variants.map((v) => {
              const val = v[row.key] || 0;
              return (
                <div key={`${row.key}-${v.key}`} onClick={() => handleClick(v)} style={valStyle(v, { color: val > 0 ? '#e2e8f0' : '#3d4f6b', ...anim((idx + 1) * 45) })}>
                  {fmtCost(val)}
                </div>
              );
            })}
          </React.Fragment>
        ))}

        {/* CO₂ */}
        <div style={{ ...labelBase, borderBottom: 'none', ...anim(280) }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', flexShrink: 0, display: 'inline-block' }} />
          CO₂
        </div>
        {variants.map((v) => (
          <div key={`co2-${v.key}`} onClick={() => handleClick(v)} style={valStyle(v, { borderBottom: 'none', ...anim(280) })}>
            {v.co2_mt ? `~${Math.round(v.co2_mt).toLocaleString()} t` : '—'}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Recalculate Modal ────────────────────────────────────────────────────────
function RecalculateModal({
  open, step, onClose, onNext, onBack, onCalculate, onDone,
  hubs, calcState, setCalcState,
  liveBunker, loadingCalc, calcError,
  result, selectedKey, onSelectKey,
  lngGateOpen, setLngGateOpen,
  isMobile,
}) {
  if (!open) return null;

  const { fromHub, toHub, tcRate, hmValue, fuelType, bunkerPrice,
    speedKts, consumptionLaden, portCosts, canalApplicable, canalDues,
    awrpApplicableVariants, awrpDays, awrpRate } = calcState;

  const set = (key, val) => setCalcState(s => ({ ...s, [key]: val }));

  // ── Source tag helpers ──
  const liveDefault = fuelType === 'IFO380' ? liveBunker.ifo380 : liveBunker.vlsfo;

  function bunkerTag() {
    if (liveBunker.error || liveDefault === null) return 'input';
    return bunkerPrice === liveDefault ? 'live' : 'input';
  }
  function portTag()  { return portCosts  === PORT_COSTS_DEFAULT  ? 'indicative' : 'input'; }
  function canalTag() { return canalDues  === CANAL_DUES_DEFAULT  ? 'indicative' : 'input'; }

  const bunkerModified  = bunkerTag() === 'input' && liveDefault !== null && !liveBunker.error;
  const portModified    = portTag()   === 'input';
  const canalModified   = canalApplicable && canalTag() === 'input';

  // ── Step 1 validity ──
  const step1Valid = fromHub && toHub && fromHub !== toHub
    && parseNum(tcRate) !== null && parseNum(tcRate) > 0
    && parseNum(hmValue) !== null && parseNum(hmValue) > 0;

  // ── AWRP mode detection ──
  // Mode B (per-variant) when a previous result exists for the same hubs with 2+ variants
  const prevVariants = result?.variants || [];
  const usePerVariantAwrp = prevVariants.length >= 2
    && result?.from_hub === fromHub
    && result?.to_hub   === toHub;

  const isAwrpOn = awrpApplicableVariants.length > 0;

  // ── AWRP live calc (shared Days/Rate apply to each ticked variant equally) ──
  const hmNum   = parseNum(hmValue) || 0;
  const daysNum = parseNum(awrpDays) || 0;
  const rateNum = parseNum(awrpRate) || 0;
  const awrpPerVariant = hmNum && daysNum && rateNum ? hmNum * (rateNum / 100) * daysNum : 0;
  const awrpTotal      = isAwrpOn ? awrpPerVariant : 0;

  // ── Fuel chip handler ──
  const preFuelRef = useRef(fuelType);
  const handleFuelChip = (f) => {
    if (f === 'LNG') {
      preFuelRef.current = fuelType;
      setLngGateOpen(true);
      return;
    }
    // Update bunker price default when switching fuel
    const newDefault = f === 'IFO380' ? liveBunker.ifo380 : liveBunker.vlsfo;
    setCalcState(s => ({
      ...s,
      fuelType: f,
      bunkerPrice: newDefault ?? s.bunkerPrice,
    }));
  };

  // ── Timestamp display ──
  const updatedAt = liveBunker.updatedAt
    ? new Date(liveBunker.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

  // ── Step 3 breakdown column count ──
  const variants  = result?.variants?.slice(0, 3) || [];
  const bestKey   = variants[0]?.key;

  const breakdownRows = [
    { key: 'bunker_cost', label: 'Bunker',  tag: bunkerTag(),  dot: '#3B82F6' },
    { key: 'tc_cost',     label: 'TC',      tag: 'input',      dot: '#0EA5E9' },
    { key: 'canal_cost',  label: 'Canal',   tag: canalTag(),   dot: '#8B5CF6' },
    { key: 'awrp_cost',   label: 'AWRP',    tag: 'input',      dot: '#EF4444' },
    { key: 'port_cost',   label: 'Port',    tag: portTag(),    dot: '#6B7280' },
  ];

  const colCount = variants.length + 1; // label + variants

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="recalc-backdrop" onClick={onClose} />

      <div className="recalc-modal" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="recalc-modal-header">
          <div className="recalc-modal-title">Route Cost Calculator</div>
          <button className="recalc-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Progress dots */}
        <div className="recalc-progress">
          {[1, 2, 3].map((s, i) => (
            <React.Fragment key={s}>
              <span className={`recalc-progress-dot ${step > s ? 'done' : step === s ? 'active' : ''}`} />
              {i < 2 && <span className={`recalc-progress-line ${step > s ? 'done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* ─── Step 1 ─────────────────────────────────────────────────────── */}
        {step === 1 && (
          <div className="recalc-step">
            <div className="recalc-row-2">
              {/* Loading region */}
              <div>
                <div className="recalc-field-label">Loading Region</div>
                <select
                  className="recalc-select"
                  value={fromHub}
                  onChange={e => set('fromHub', e.target.value)}
                >
                  <option value="" disabled>Select hub…</option>
                  {hubs.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
              </div>
              {/* Discharge region */}
              <div>
                <div className="recalc-field-label">Discharge Region</div>
                <select
                  className="recalc-select"
                  value={toHub}
                  onChange={e => set('toHub', e.target.value)}
                >
                  <option value="" disabled>Select hub…</option>
                  {hubs.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
              </div>
            </div>

            <div className="recalc-row-2">
              {/* TC Rate */}
              <div>
                <div className="recalc-field-label">TC Rate ($/day)</div>
                <NumericInput
                  value={tcRate}
                  onChange={v => set('tcRate', v)}
                  placeholder="e.g. 22 000"
                  prefix="$"
                  step={500}
                />
              </div>
              {/* H&M Value */}
              <div>
                <div className="recalc-field-label">H&amp;M Value ($)</div>
                <NumericInput
                  value={hmValue}
                  onChange={v => set('hmValue', v)}
                  placeholder="e.g. 62 000 000"
                  prefix="$"
                  step={1_000_000}
                />
              </div>
            </div>

            {/* H&M callout */}
            <div className="recalc-callout">
              H&amp;M value only. Do not include Increased Value (IV) or Disbursements. AWRP is calculated against H&amp;M, not Total Insured Value.
            </div>
          </div>
        )}

        {/* ─── Step 2 ─────────────────────────────────────────────────────── */}
        {step === 2 && (
          <div className="recalc-step">

            {/* Fuel type chips */}
            <div>
              <div className="recalc-field-label">Fuel Type</div>
              <div className="recalc-fuel-chips">
                {MODAL_FUEL_OPTS.map(f => (
                  <button
                    key={f}
                    className={`recalc-fuel-chip${fuelType === f ? ' active' : ''}${f === 'LNG' ? ' gated' : ''}`}
                    onClick={() => handleFuelChip(f)}
                  >
                    {f === 'LNG' ? '⬡ LNG' : f}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 5 }}>
                Main fuel only. Auxiliary MGO and pilot fuel consumption excluded.
              </div>
              <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 3 }}>
                <SourceTag type="indicative" /> IMO MEPC.364(79) · CO₂ emission factors
              </div>
            </div>

            {/* Bunker Price */}
            <div>
              <div className="recalc-field-label">
                Bunker Price ($/MT)
                {bunkerModified && <span className="recalc-mod-dot" />}
                <SourceTag type={bunkerTag()} />
              </div>
              <NumericInput
                value={bunkerPrice}
                onChange={v => set('bunkerPrice', v)}
                placeholder={liveBunker.error ? 'Enter manually' : 'Loading…'}
                prefix="$"
                step={10}
              />
              {bunkerTag() === 'live' && updatedAt && (
                <div className="recalc-src-label">
                  Live · Global 20 {fuelType} · Ship &amp; Bunker · {updatedAt}
                </div>
              )}
              {liveBunker.error && bunkerPrice === null && (
                <div style={{ fontSize: 11, color: 'var(--gold-bright)', marginTop: 4 }}>
                  Unable to fetch live bunker price — enter manually
                </div>
              )}
            </div>

            <div className="recalc-row-2">
              {/* Warranted Speed */}
              <div>
                <div className="recalc-field-label">Warranted Speed (kts)</div>
                <NumericInput
                  value={speedKts}
                  onChange={v => set('speedKts', v ?? 14)}
                  placeholder="e.g. 14"
                  min={6} max={20} step={0.5}
                />
              </div>
              {/* Warranted Consumption */}
              <div>
                <div className="recalc-field-label">Warranted Consumption (MT/day)</div>
                <NumericInput
                  value={consumptionLaden}
                  onChange={v => set('consumptionLaden', v ?? 75)}
                  placeholder="e.g. 75"
                  min={10} max={200} step={5}
                />
              </div>
            </div>

            {/* Port Costs */}
            <div>
              <div className="recalc-field-label">
                Port Costs ($)
                {portModified && <span className="recalc-mod-dot" />}
                <SourceTag type={portTag()} />
              </div>
              <NumericInput
                value={portCosts}
                onChange={v => set('portCosts', v ?? PORT_COSTS_DEFAULT)}
                placeholder="80 000"
                prefix="$"
                step={5_000}
              />
              {portTag() === 'indicative' && (
                <div className="recalc-src-label">Indicative · Estimate — load + discharge combined</div>
              )}
            </div>

            {/* Canal dues checkbox */}
            <div>
              <label className="recalc-checkbox-row">
                <input
                  type="checkbox"
                  checked={canalApplicable}
                  onChange={e => set('canalApplicable', e.target.checked)}
                />
                <span className="recalc-checkbox-label">Any canal dues applicable?</span>
              </label>

              {canalApplicable && (
                <div style={{ marginTop: 10 }}>
                  <div className="recalc-field-label" style={{ marginBottom: 6 }}>
                    Canal Dues ($)
                    {canalModified && <span className="recalc-mod-dot" />}
                    <SourceTag type={canalTag()} />
                  </div>
                  <NumericInput
                    value={canalDues}
                    onChange={v => set('canalDues', v ?? CANAL_DUES_DEFAULT)}
                    placeholder="50 000"
                    prefix="$"
                    step={10_000}
                  />
                  {canalTag() === 'indicative' && (
                    <div className="recalc-src-label">Indicative · Estimate — varies by canal and vessel type</div>
                  )}
                </div>
              )}
            </div>

            {/* AWRP — Mode A (generic) or Mode B (per-variant on reopen with same hubs) */}
            <div>
              {usePerVariantAwrp ? (
                // ── Mode B: per-variant checkboxes ──────────────────────────
                <>
                  <div className="recalc-field-label" style={{ marginBottom: 8 }}>
                    Does this voyage transit a JWC-listed area on any of these routes?
                  </div>
                  {prevVariants.map(v => (
                    <label key={v.key} className="recalc-checkbox-row" style={{ marginBottom: 6 }}>
                      <input
                        type="checkbox"
                        checked={awrpApplicableVariants.includes(v.key)}
                        onChange={e => {
                          const updated = e.target.checked
                            ? [...awrpApplicableVariants, v.key]
                            : awrpApplicableVariants.filter(k => k !== v.key);
                          set('awrpApplicableVariants', updated);
                        }}
                      />
                      <span className="recalc-checkbox-label">{v.display_name}</span>
                    </label>
                  ))}
                </>
              ) : (
                // ── Mode A: single generic checkbox ─────────────────────────
                <label className="recalc-checkbox-row">
                  <input
                    type="checkbox"
                    checked={isAwrpOn}
                    onChange={e => set('awrpApplicableVariants', e.target.checked ? ['__all__'] : [])}
                  />
                  <span className="recalc-checkbox-label">Do you want to account for Additional War Risk Premium?</span>
                </label>
              )}

              {isAwrpOn && (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div className="recalc-row-2">
                    <div>
                      <div className="recalc-field-label">Days Applicable</div>
                      <NumericInput
                        value={awrpDays}
                        onChange={v => set('awrpDays', v)}
                        placeholder="e.g. 3"
                        min={0} step={1}
                      />
                    </div>
                    <div>
                      <div className="recalc-field-label">Rate (%)</div>
                      <NumericInput
                        value={awrpRate}
                        onChange={v => set('awrpRate', v)}
                        placeholder="e.g. 0.50"
                        min={0} step={0.05}
                      />
                    </div>
                  </div>

                  {awrpPerVariant > 0 && (
                    <div className="recalc-awrp-box">
                      <div className="recalc-awrp-formula">
                        H&amp;M: ${hmNum.toLocaleString('en-US')}<br />
                        Days: {daysNum} · Rate: {rateNum}%<br />
                        ${hmNum.toLocaleString('en-US')} × {rateNum}% × {daysNum}
                      </div>
                      {usePerVariantAwrp ? (
                        // Show one line per ticked variant
                        <div style={{ marginTop: 6 }}>
                          {prevVariants
                            .filter(v => awrpApplicableVariants.includes(v.key))
                            .map(v => (
                              <div key={v.key} style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--amber)', lineHeight: 1.6 }}>
                                {v.display_name}: {fmtCost(awrpPerVariant)}
                              </div>
                            ))
                          }
                        </div>
                      ) : (
                        <div className="recalc-awrp-total">{fmtCost(awrpPerVariant)}</div>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>
                        As quoted by your broker for this voyage. AWRP quotation structure varies by policy — confirm exact terms with your underwriter.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {calcError && (
              <div style={{ padding: '10px 14px', borderRadius: 5, background: 'var(--red-light)', border: '1px solid var(--red-mid)', color: 'var(--red-bright)', fontSize: 13 }}>
                ⚠ {calcError}
              </div>
            )}
          </div>
        )}

        {/* ─── Step 3 ─────────────────────────────────────────────────────── */}
        {step === 3 && (
          <div className="recalc-step">
            {/* Route cards */}
            <div className="recalc-route-cards">
              {variants.map((v) => (
                <div
                  key={v.key}
                  className={`recalc-route-card${v.key === bestKey ? ' best-card' : ''}${v.key === selectedKey ? ' selected-card' : ''}`}
                  onClick={() => onSelectKey(v.key === selectedKey ? null : v.key)}
                >
                  {v.key === bestKey && <span className="recalc-cheapest-badge">CHEAPEST</span>}
                  {variants.length > 1 && (
                    <div className="recalc-route-card-name">{v.display_name}</div>
                  )}
                  <div className="recalc-route-card-meta">{fmtNm(v.distance_nm)}</div>
                  <div className="recalc-route-card-meta">{fmtDays(v.voyage_days)}</div>
                  <div className="recalc-route-card-total">{fmtCost(v.total_cost)}</div>
                  <div className="recalc-route-card-co2">CO₂: {v.co2_mt ? `~${Math.round(v.co2_mt).toLocaleString()} t` : '—'}</div>
                </div>
              ))}
            </div>

            {/* Breakdown table — multi-col when 2+ variants */}
            <div className="recalc-breakdown">
              {/* Header */}
              {variants.length > 1 && (
                <div style={{ display: 'grid', gridTemplateColumns: `140px repeat(${variants.length}, 1fr)`, borderBottom: '2px solid var(--border-mid)' }}>
                  <div className="recalc-breakdown-label" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text2)' }}>Cost</div>
                  {variants.map(v => (
                    <div key={v.key} className="recalc-breakdown-val" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.6px', color: v.key === bestKey ? 'var(--amber)' : 'var(--text2)', fontFamily: 'var(--font-sans)' }}>
                      {v.display_name}
                    </div>
                  ))}
                </div>
              )}

              {breakdownRows.map((row) => (
                <div key={row.key} className="recalc-breakdown-row" style={{ gridTemplateColumns: `140px repeat(${variants.length}, 1fr)`, display: 'grid' }}>
                  <div className="recalc-breakdown-label">
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: row.dot, flexShrink: 0, display: 'inline-block' }} />
                    {row.label}
                    <SourceTag type={row.tag} />
                  </div>
                  {variants.map(v => {
                    const val = v[row.key] || 0;
                    return (
                      <div key={v.key} className={`recalc-breakdown-val${val === 0 ? ' zero' : ''}`}>
                        {fmtCost(val)}
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Total */}
              <div className="recalc-breakdown-total" style={{ gridTemplateColumns: `140px repeat(${variants.length}, 1fr)`, display: 'grid' }}>
                <div className="recalc-breakdown-label">Total</div>
                {variants.map(v => (
                  <div key={v.key} className="recalc-breakdown-val">{fmtCost(v.total_cost)}</div>
                ))}
              </div>
            </div>

            {/* CO₂ delta — only when 2+ variants */}
            {variants.length >= 2 && (() => {
              const best  = variants[0];
              const worst = variants[variants.length - 1];
              const delta = Math.round((worst.co2_mt || 0) - (best.co2_mt || 0));
              if (!delta || !best.co2_mt) return null;
              return (
                <div className="recalc-co2-delta">
                  {best.display_name} emits ~{Math.round(best.co2_mt).toLocaleString()} t CO₂.{' '}
                  {worst.display_name} adds ~{delta.toLocaleString()} t more.{' '}
                  <span style={{ fontSize: 10, opacity: 0.7 }}>Source: IMO MEPC.364(79) · main fuel only</span>
                </div>
              );
            })()}
          </div>
        )}

        {/* Footer navigation */}
        <div className="recalc-footer">
          <div>
            {step > 1 && (
              <button className="recalc-btn recalc-btn-back" onClick={onBack}>← Back</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {step === 1 && (
              <>
                <button className="recalc-btn recalc-btn-ghost" disabled>Calculate</button>
                <button
                  className="recalc-btn recalc-btn-next"
                  disabled={!step1Valid}
                  onClick={onNext}
                >
                  Next →
                </button>
              </>
            )}
            {step === 2 && (
              <button
                className="recalc-btn recalc-btn-calculate"
                disabled={loadingCalc || !bunkerPrice || !speedKts || !consumptionLaden || !portCosts}
                onClick={onCalculate}
              >
                {loadingCalc ? 'Calculating…' : 'Calculate'}
              </button>
            )}
            {step === 3 && (
              <button className="recalc-btn recalc-btn-done" onClick={onDone}>Done ✓</button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Left Rail ────────────────────────────────────────────────────────────────
function LeftRail({
  calcState, setCalcState, hubs, liveBunker,
  onRecalculate, loadingCalc,
}) {
  const { fromHub, toHub, tcRate, hmValue, fuelType, bunkerPrice,
    speedKts, consumptionLaden, portCosts, canalApplicable, canalDues,
    awrpApplicableVariants, awrpDays, awrpRate } = calcState;

  const set = (key, val) => setCalcState(s => ({ ...s, [key]: val }));

  const liveDefault = fuelType === 'IFO380' ? liveBunker.ifo380 : liveBunker.vlsfo;
  const bunkerTag   = () => (!liveBunker.error && liveDefault !== null && bunkerPrice === liveDefault) ? 'live' : 'input';
  const portTag     = () => portCosts  === PORT_COSTS_DEFAULT  ? 'indicative' : 'input';
  const canalTag    = () => canalDues  === CANAL_DUES_DEFAULT  ? 'indicative' : 'input';

  const fromName = hubs.find(h => h.id === fromHub)?.name || fromHub;
  const toName   = hubs.find(h => h.id === toHub)?.name   || toHub;

  const isAwrpOn  = awrpApplicableVariants.length > 0;
  const awrpTotal = isAwrpOn && hmValue && awrpDays && awrpRate
    ? (parseNum(hmValue) || 0) * ((parseNum(awrpRate) || 0) / 100) * (parseNum(awrpDays) || 0)
    : 0;

  const railRowStyle = { fontSize: 13, color: 'var(--text)', fontFamily: 'var(--font-mono)', marginTop: 2 };

  return (
    <aside className="recalc-rail">
      <div className="recalc-rail-header">
        <div className="recalc-rail-title">Voyage Parameters</div>
      </div>

      <div className="recalc-rail-body">
        {/* Route */}
        <div className="recalc-rail-row">
          <div className="recalc-rail-row-label">Route</div>
          <div style={{ ...railRowStyle, fontFamily: 'var(--font-sans)', fontSize: 12 }}>
            {fromName} → {toName}
          </div>
        </div>

        <div className="recalc-rail-divider" />

        {/* TC Rate */}
        <div className="recalc-rail-row">
          <div className="recalc-rail-row-label">TC Rate ($/day) <SourceTag type="input" /></div>
          <NumericInput
            value={tcRate}
            onChange={v => set('tcRate', v)}
            placeholder="—"
            prefix="$"
            step={500}
            className="recalc-num-input"
          />
        </div>

        {/* H&M Value */}
        <div className="recalc-rail-row">
          <div className="recalc-rail-row-label">H&amp;M Value ($) <SourceTag type="input" /></div>
          <NumericInput
            value={hmValue}
            onChange={v => set('hmValue', v)}
            placeholder="—"
            prefix="$"
            step={1_000_000}
            className="recalc-num-input"
          />
        </div>

        <div className="recalc-rail-divider" />

        {/* Fuel type */}
        <div className="recalc-rail-row">
          <div className="recalc-rail-row-label">Fuel Type</div>
          <div style={{ ...railRowStyle }}>{fuelType}</div>
        </div>

        {/* Bunker price */}
        <div className="recalc-rail-row">
          <div className="recalc-rail-row-label">
            Bunker Price
            {bunkerTag() !== 'live' && bunkerPrice !== liveDefault && liveDefault !== null && !liveBunker.error && (
              <span className="recalc-mod-dot" />
            )}
            <SourceTag type={bunkerTag()} />
          </div>
          <NumericInput
            value={bunkerPrice}
            onChange={v => set('bunkerPrice', v)}
            placeholder="—"
            prefix="$"
            step={10}
            className="recalc-num-input"
          />
          {bunkerTag() === 'live' && (
            <div className="recalc-rail-src">Global 20 {fuelType} · Ship &amp; Bunker</div>
          )}
        </div>

        {/* Speed */}
        <div className="recalc-rail-row">
          <div className="recalc-rail-row-label">Speed (kts) <SourceTag type="input" /></div>
          <NumericInput
            value={speedKts}
            onChange={v => set('speedKts', v ?? 14)}
            placeholder="—"
            min={6} max={20} step={0.5}
            className="recalc-num-input"
          />
        </div>

        {/* Consumption */}
        <div className="recalc-rail-row">
          <div className="recalc-rail-row-label">Consumption (MT/day) <SourceTag type="input" /></div>
          <NumericInput
            value={consumptionLaden}
            onChange={v => set('consumptionLaden', v ?? 75)}
            placeholder="—"
            min={10} max={200} step={5}
            className="recalc-num-input"
          />
        </div>

        <div className="recalc-rail-divider" />

        {/* Port costs */}
        <div className="recalc-rail-row">
          <div className="recalc-rail-row-label">
            Port Costs
            {portTag() === 'input' && <span className="recalc-mod-dot" />}
            <SourceTag type={portTag()} />
          </div>
          <NumericInput
            value={portCosts}
            onChange={v => set('portCosts', v ?? PORT_COSTS_DEFAULT)}
            prefix="$"
            step={5_000}
            className="recalc-num-input"
          />
          {portTag() === 'indicative' && (
            <div className="recalc-rail-src">Estimate — load + discharge</div>
          )}
        </div>

        {/* Canal dues (conditional) */}
        {canalApplicable && (
          <div className="recalc-rail-row">
            <div className="recalc-rail-row-label">
              Canal Dues
              {canalTag() === 'input' && <span className="recalc-mod-dot" />}
              <SourceTag type={canalTag()} />
            </div>
            <NumericInput
              value={canalDues}
              onChange={v => set('canalDues', v ?? CANAL_DUES_DEFAULT)}
              prefix="$"
              step={10_000}
              className="recalc-num-input"
            />
          </div>
        )}

        {/* AWRP (conditional) */}
        {isAwrpOn && (
          <div className="recalc-rail-row">
            <div className="recalc-rail-row-label">AWRP <SourceTag type="input" /></div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
              {awrpDays}d × {awrpRate}% × H&amp;M
            </div>
            {awrpTotal > 0 && (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--amber)', marginTop: 2 }}>
                {fmtCost(awrpTotal)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="recalc-rail-footer">
        <button
          className="recalc-rail-btn"
          disabled={loadingCalc}
          onClick={onRecalculate}
        >
          {loadingCalc ? 'Calculating…' : 'Recalculate'}
        </button>
      </div>
    </aside>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function RoutesModule() {
  const { user }       = useUser();
  const { openSignIn } = useClerk();

  const [hubs, setHubs] = useState([]);

  // ── Canonical calculation state (shared modal ↔ rail) ──
  const [calcState, setCalcState] = useState({
    fromHub:          'AG',
    toHub:            'N_EUROPE',
    tcRate:           null,
    hmValue:          null,
    fuelType:         'VLSFO',
    bunkerPrice:      null,
    speedKts:         14,
    consumptionLaden: 75,
    portCosts:        PORT_COSTS_DEFAULT,
    canalApplicable:  false,
    canalDues:               CANAL_DUES_DEFAULT,
    awrpApplicableVariants:  [],   // [] = off; ['__all__'] = all variants; ['via_suez', ...] = specific keys
    awrpDays:                null,
    awrpRate:                null,
  });

  // ── Live bunker data (source tag logic) ──
  const [liveBunker, setLiveBunker] = useState({ vlsfo: null, ifo380: null, updatedAt: null, fetched: false, error: false });

  // ── UI state ──
  const [modalOpen,      setModalOpen]      = useState(false);
  const [modalStep,      setModalStep]      = useState(1);
  const [hasCalculated,  setHasCalculated]  = useState(false);
  const [lngGateOpen,    setLngGateOpen]    = useState(false);

  // ── Results ──
  const [result,      setResult]      = useState(null);
  const [loadingCalc, setLoadingCalc] = useState(false);
  const [calcError,   setCalcError]   = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const gridRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Load hubs + check session flag on mount ──
  useEffect(() => {
    fetchHubs().then(setHubs).catch(() => setHubs([]));
    const seen = sessionStorage.getItem(SESSION_FLAG);
    if (!seen) {
      setModalOpen(true);
      setModalStep(1);
      fetchLiveBunker();
    }
  }, []);

  const fetchLiveBunker = useCallback(async () => {
    const data = await fetchBunkerGlobal20();
    setLiveBunker({ ...data, fetched: true });

    // Pre-fill bunker price default if user hasn't set one
    setCalcState(s => {
      if (s.bunkerPrice !== null) return s; // already set — respect it
      const def = s.fuelType === 'IFO380' ? data.ifo380 : data.vlsfo;
      return { ...s, bunkerPrice: def ?? s.bunkerPrice };
    });
  }, []);

  // ── Open modal ──
  const openModal = () => {
    setModalOpen(true);
    setModalStep(1);
    fetchLiveBunker();
  };

  // ── Close modal ──
  const closeModal = () => {
    sessionStorage.setItem(SESSION_FLAG, '1');
    setModalOpen(false);
    setCalcError(null);
  };

  // ── Execute calculation (used by both modal and rail) ──
  const runCalculation = useCallback(async (state) => {
    const {
      fromHub, toHub, fuelType, speedKts, consumptionLaden, bunkerPrice,
      tcRate, hmValue, portCosts, canalApplicable, canalDues,
      awrpApplicableVariants, awrpDays, awrpRate,
    } = state;
    const awrpOn = awrpApplicableVariants.length > 0;

    setLoadingCalc(true);
    setCalcError(null);

    try {
      const r = await fetchRouteCosts({
        fromHub, toHub, fuelType,
        speedKts:         Number(speedKts),
        consumptionLaden: Number(consumptionLaden),
        bunkerPrice:      Number(bunkerPrice),
        tcRate:           Number(tcRate),
        hmValue:          Number(hmValue),
        canalDues:        canalApplicable ? Number(canalDues) : 0,
        portCosts:               Number(portCosts),
        awrpApplicableVariants:  awrpApplicableVariants,
        awrpDays:                awrpOn ? Number(awrpDays) : 0,
        awrpRatePct:             awrpOn ? Number(awrpRate) : 0,
      });
      setResult(r);
      setHasCalculated(true);
      setSelectedKey(r?.variants?.[0]?.key || null);
      return true;
    } catch (e) {
      setCalcError(e.message || 'Calculation failed');
      return false;
    } finally {
      setLoadingCalc(false);
    }
  }, []);

  // ── Modal Calculate button ──
  const handleModalCalculate = async () => {
    const ok = await runCalculation(calcState);
    if (ok) setModalStep(3);
  };

  // ── Modal Done ──
  const handleModalDone = () => {
    sessionStorage.setItem(SESSION_FLAG, '1');
    setModalOpen(false);
    setCalcError(null);
    setTimeout(() => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  };

  // ── Rail Recalculate ──
  const handleRailRecalculate = () => runCalculation(calcState);

  const variants  = (result?.variants || []).slice(0, 3);
  const best      = variants[0];
  const worst     = variants[variants.length - 1];
  const fromName  = hubs.find(h => h.id === calcState.fromHub)?.name || calcState.fromHub;
  const toName    = hubs.find(h => h.id === calcState.toHub)?.name   || calcState.toHub;

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* LNG gate modal */}
      {lngGateOpen && (
        <ProWaitlistModal
          interestSource="lng_fuel_routes"
          onClose={() => setLngGateOpen(false)}
        />
      )}

      {/* Recalculate modal */}
      <RecalculateModal
        open={modalOpen}
        step={modalStep}
        onClose={closeModal}
        onNext={() => setModalStep(s => s + 1)}
        onBack={() => setModalStep(s => s - 1)}
        onCalculate={handleModalCalculate}
        onDone={handleModalDone}
        hubs={hubs}
        calcState={calcState}
        setCalcState={setCalcState}
        liveBunker={liveBunker}
        loadingCalc={loadingCalc}
        calcError={calcError}
        result={result}
        selectedKey={selectedKey}
        onSelectKey={setSelectedKey}
        lngGateOpen={lngGateOpen}
        setLngGateOpen={setLngGateOpen}
        isMobile={isMobile}
      />

      {/* Top bar */}
      <div style={{ background: 'var(--card)', padding: isMobile ? '12px 16px' : '12px 28px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, boxShadow: 'var(--shadow-sm)' }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 18 : 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>Route Cost Comparator</div>
          {!isMobile && <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 2 }}>Total voyage cost · Bunker · Canal · AWRP · TC</div>}
        </div>
      </div>

      {/* Body layout */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>

        {/* Left rail — hidden until first Calculate */}
        {hasCalculated && !isMobile && (
          <LeftRail
            calcState={calcState}
            setCalcState={setCalcState}
            hubs={hubs}
            liveBunker={liveBunker}
            onRecalculate={handleRailRecalculate}
            loadingCalc={loadingCalc}
          />
        )}

        {/* Main area */}
        <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>

          {/* Empty state — before first Calculate */}
          {!hasCalculated && (
            <div className="recalc-empty">
              <div className="recalc-empty-icon">🚢</div>
              <div className="recalc-empty-title">Route Cost Comparator</div>
              <div className="recalc-empty-sub">Enter your voyage parameters to calculate costs across all available routes</div>
              <button className="recalc-empty-btn" onClick={openModal}>Recalculate</button>
            </div>
          )}

          {/* After first Calculate: results area */}
          {hasCalculated && (
            <>
              {/* Recalculate trigger bar */}
              <div className="recalc-trigger-bar">
                <button className="recalc-trigger-btn" onClick={openModal}>⟳ Recalculate</button>
              </div>

              {/* Mobile rail (inline) */}
              {hasCalculated && isMobile && (
                <div style={{ borderBottom: '1px solid var(--border)' }}>
                  <LeftRail
                    calcState={calcState}
                    setCalcState={setCalcState}
                    hubs={hubs}
                    liveBunker={liveBunker}
                    onRecalculate={handleRailRecalculate}
                    loadingCalc={loadingCalc}
                  />
                </div>
              )}

              {/* Loading */}
              {loadingCalc && (
                <div style={{ padding: isMobile ? '16px 14px' : '20px 28px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16 }}>
                  {[0, 1].map(i => (
                    <div key={i} style={{ flex: 1, borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
                      <div style={{ height: 6 }} className="fv7-shimmer" />
                      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div className="fv7-shimmer" style={{ height: 22, width: '65%' }} />
                        <div className="fv7-shimmer" style={{ height: 13, width: '40%' }} />
                        <div className="fv7-shimmer" style={{ height: 44, width: '55%', marginTop: 6 }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error */}
              {calcError && !loadingCalc && (
                <div style={{ padding: isMobile ? '16px 14px' : '20px 28px' }}>
                  <div style={{ padding: '14px 18px', borderRadius: 'var(--radius-md)', background: 'var(--red-light)', border: '1px solid var(--red-mid)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ color: 'var(--red)', fontSize: 16, flexShrink: 0 }}>⚠</span>
                    <div style={{ color: 'var(--red)', fontWeight: 600, fontSize: 14 }}>{calcError}</div>
                  </div>
                </div>
              )}

              {/* Route grid */}
              {!loadingCalc && result && variants.length > 0 && (
                <div ref={gridRef} tabIndex={-1} style={{ padding: isMobile ? '16px 14px' : '20px 28px 4px' }}>
                  <div style={{ border: '1px solid #1e2d44', borderRadius: 10 }}>
                    <RouteGrid
                      variants={variants}
                      selectedKey={selectedKey}
                      onSelectKey={setSelectedKey}
                      isMobile={isMobile}
                    />
                  </div>
                </div>
              )}

              {/* Route map */}
              {!loadingCalc && result && variants.length > 0 && (
                <div style={{ padding: isMobile ? '0 14px 16px' : '0 28px 20px' }}>
                  <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                    <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Route visualisation</div>
                    </div>
                    <RouteMap
                      result={result}
                      fromHub={calcState.fromHub}
                      toHub={calcState.toHub}
                      hubs={hubs}
                      selectedKey={selectedKey}
                      onSelectKey={setSelectedKey}
                      isMobile={isMobile}
                    />
                  </div>
                </div>
              )}

              {/* Next Cargo Intel */}
              {!loadingCalc && result && calcState.toHub && (
                <NextCargoSection toHub={calcState.toHub} hubs={hubs} user={user} onSignIn={() => openSignIn()} isMobile={isMobile} />
              )}

              {/* Key metrics summary bar */}
              {!loadingCalc && result && variants.length >= 2 && !isMobile && (
                <div style={{ padding: '4px 28px 12px', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--amber)', fontWeight: 700 }}>{fmtM(best.total_cost)}</span>
                    {' '}<span style={{ color: 'var(--text2)' }}>cheapest via {best.display_name}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--green-bright)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                    Saves {fmtM(worst.total_cost - best.total_cost)}
                  </div>
                </div>
              )}

              {/* Footer */}
              {result && (
                <div style={{ padding: isMobile ? '10px 14px 20px' : '10px 28px 20px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>FATHOMVOYAGE.COM · INDICATIVE ONLY</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>Confirm all costs with brokers before fixing</div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
